/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { useEffect, useRef, useState, useCallback } from "react";
import styles from "./styles.module.scss";
import { AssistantControlForm, CVComparisonTable, CVUpload, ExperienceLevelForm, MessageDisplay, MessageForm, OptionsForm, QuestionForm, RecruitmentOptions, Spinner, UploadedCVDisplay, LoadingIndicator } from "components";
import type { InputHints } from "helpers/assistantInputHints";
import { ArrowDownIcon } from "assets";
import { JobDescription } from "components/job-description";
import { formatApiResponseToQuestions } from "helpers/formatJDRequirements";
import { mapJsonToJobCriteriaData } from "utils/jobCriteria";
import EnhancedJobCriteria from "components/criteria";
import QuestionsComponent from "components/questions";
import { MultipleChoiceForm } from "components/add-multiple-questions";
import TimelineComponent from "components/timeline-update";
import JobAssessment from "components/job-assessment";
import { useConversationProcessing } from "hooks/useConversationProcessing";
import { useResponseProcessing } from "hooks/useResponseProcessing";
import { TechnicalCompetencePayload } from "hooks";
import { ErrorWithRetry } from "components/chat/error-with-retry";
import { ShortOnCreditModal, BuyAdditionalCreditModal } from "pages/pricing-v2/components";
import { HiringProgressModal } from "pages/pricing-v2/components/hiring-progress-modal";
import { useCredits } from "hooks/useCredits";
import { isCustomQuestionStage } from "helpers/customQuestionDraft";
import { ArrowDown } from "lucide-react";
import type { Message, ConversationOption, SalaryDetails } from './types';

// Declared once in ./types and re-exported here so the many `from 'components'`
// imports keep working.
export type {
  Message,
  MessageComponentType,
  ConversationOption,
  ConversationData,
  SalaryDetails,
} from './types';

interface ChatMessagesProps {
  className?: string;
  placeholder?: string;
  systemName?: string;
  userName?: string;
  initialMessages: Message[];
  messages: Message[];
  setMessages: (value: Message[] | ((prev: Message[]) => Message[])) => void;
  fetchAssessmentSessions: () => void;

  // All state props from parent
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  showInput: boolean;
  setShowInput: (show: boolean) => void;
  inputHints: InputHints | null;
  setInputHints: (hints: InputHints | null) => void;
  conversationState: string;
  setConversationState: (state: string) => void;
  currentOptions: ConversationOption[] | null;
  setCurrentOptions: (options: ConversationOption[] | null) => void;
  showOption: boolean;
  setShowOption: (show: boolean) => void;
  formData: Record<string, string | string[]>;
  setFormData: (data: Record<string, string | string[]>) => void;
  salaryDetails: SalaryDetails;
  setSalaryDetails: (details: SalaryDetails) => void;
  assessmentId: string | null;
  setAssessmentId: (id: string | null) => void;
  assistantId: string | null;
  setAssistantId: (id: string | null) => void;
  expectations: any;
  setExpectations: (expectations: any) => void;
  assessmentStarted: boolean;
  setAssessmentStarted: (started: boolean) => void;
  lastProcessedResponse: any;
  setLastProcessedResponse: (response: any) => void;
  isInitialized: boolean;
  setIsInitialized: (initialized: boolean) => void;

  // "Pick Up Where You Left Off": auto-select the "hire for a role" option
  // (flow_2) and prefill the role input with the visitor's landing-page intent.
  autoSelectScratch?: boolean;
  onScratchSelected?: () => void;
  prefillRole?: string | null;
  onPrefillConsumed?: () => void;
}

const ChatMessages = React.forwardRef<any, ChatMessagesProps>(
  ({
    initialMessages,
    messages,
    setMessages,
    systemName,
    placeholder,
    className,
    fetchAssessmentSessions,
    // State props
    isLoading,
    setIsLoading,
    showInput,
    setShowInput,
    inputHints,
    setInputHints,
    conversationState,
    setConversationState,
    currentOptions,
    setCurrentOptions,
    showOption,
    setShowOption,
    formData,
    setFormData,
    salaryDetails,
    setSalaryDetails,
    assessmentId,
    setAssessmentId,
    assistantId,
    setAssistantId,
    expectations,
    setExpectations,
    assessmentStarted,
    setAssessmentStarted,
    lastProcessedResponse,
    setLastProcessedResponse,
    isInitialized,
    setIsInitialized,
    autoSelectScratch,
    onScratchSelected,
    prefillRole,
    onPrefillConsumed,
  }, ref) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const isNearBottomRef = useRef(true);
    const [assessmentStatus, setAssessmentStatus] = React.useState<'idle' | 'initial' | 'ready'>('idle');
    const currentTechnicalQuestionsRef = useRef<TechnicalCompetencePayload | null>(null);
    const [showBuyCreditModal, setShowBuyCreditModal] = React.useState(false);
    const { balance: creditsAvailable } = useCredits();

    const handleTechnicalQuestionsChange = React.useCallback((questions: TechnicalCompetencePayload | null) => {
      currentTechnicalQuestionsRef.current = questions;
    }, []);

    const { historicalFormData, conversationHistoryLoading, handleFormChange, handleSalaryChange, submitQuestion, candidatesToInvite: historyCandidatesToInvite, handleCandidatesToInviteChange } = useConversationProcessing({
      assessmentId,
      formData,
      setFormData,
      salaryDetails,
      setSalaryDetails,
      expectations,
      setMessages,
      messages,
      setCurrentOptions,
      setShowOption,
      setShowInput,
      setInputHints,
      setIsSubmittedQuestions: (value) => setIsSubmittedQustions(value),
      setConversationState,
      initialMessages,
      isInitialized,
      setAssessmentId,
      setIsInitialized,
      assessmentStatus,
      assistantId,
      setAssistantId
    });

    const { isSubmittedQuestions, setIsSubmittedQustions, imageUploadScroll, setImageUploadScroll, handleMessageSend, handleOptionSelect, handleExperienceLevelSubmit, handleControlSubmit, sendRequest, handleRetry, handleStartNewConversation, showShortOnCreditModal, setShowShortOnCreditModal, showHiringProgressModal: showHiringProgressFromHook, setShowHiringProgressModal: setShowHiringProgressFromHook, creditErrorInfo, setCreditErrorInfo, loadingPhase, candidatesToInvite, setCandidatesToInvite } = useResponseProcessing({
      assessmentId,
      setAssessmentId,
      assistantId,
      setAssistantId,
      conversationState,
      setConversationState,
      setMessages,
      messages,
      currentOptions,
      setCurrentOptions,
      setShowOption,
      setShowInput,
      setIsLoading,
      setAssessmentStarted,
      assessmentStarted,
      assessmentStatus,
      setAssessmentStatus,
      currentTechnicalQuestionsRef,
      setInputHints
    });

    const hasAutoAdministered = useRef(false);

    useEffect(() => {
      if (hasAutoAdministered.current) return;

      const params = new URLSearchParams(window.location.search);
      if (params.get('autoAdminister') !== 'true') return;

      // display_results is the state set by useConversationProcessing when the
      // CV comparison table is active. administer_assessment lives in
      // message.componentData.options (not currentOptions), so we detect the
      // stage via conversationState instead.
      if (conversationState !== 'display_results') return;

      hasAutoAdministered.current = true;

      // Clear param without triggering React Router re-render
      const url = new URL(window.location.href);
      url.searchParams.delete('autoAdminister');
      window.history.replaceState({}, '', url);

      handleOptionSelect('administer_assessment');
    }, [conversationState]);

    // "Pick Up Where You Left Off" → Continue: auto-select the "hire for a role"
    // option once. The parent clears the trigger via onScratchSelected so this
    // never re-fires when the component remounts on the new assessment id.
    const hasAutoSelectedScratch = useRef(false);
    useEffect(() => {
      if (!autoSelectScratch || hasAutoSelectedScratch.current) return;
      hasAutoSelectedScratch.current = true;
      // Ensure flow_2 is set before the option fires (handleOptionSelect reads it).
      localStorage.setItem('hiringAssistantFlowType', 'flow_2');
      handleOptionSelect('scratch');
      onScratchSelected?.();
    }, [autoSelectScratch]);

    // The chat subtree is force-remounted (`key={currentAssessmentId}` in the
    // parent) as soon as the new assessment id lands in the URL — which happens
    // one render *after* the assistant asks for the role. Clearing the carried-
    // over role the instant the input applied it therefore left the remounted
    // input empty, so the parent keeps it until the role is actually sent. By
    // then `conversationState` has moved off `get_role` and it is no longer
    // offered anyway.
    const handleRoleAwareSend = React.useCallback(
      (message: string, attachments?: File[]) => {
        if (conversationState === 'get_role') onPrefillConsumed?.();
        handleMessageSend(message, attachments, inputHints?.documentMode === true);
      },
      [conversationState, inputHints?.documentMode, onPrefillConsumed, handleMessageSend]
    );

    const handleInsufficientCredits = React.useCallback((creditsRequired: number, creditsAvail: number) => {
      setCreditErrorInfo({ creditsRequired, creditsAvailable: creditsAvail });
      setShowShortOnCreditModal(true);
    }, [setCreditErrorInfo, setShowShortOnCreditModal]);

    const lastQuestionMessageId = [...messages].reverse().find(
      (m) => m.componentType === 'addSingleQuestion' || m.componentType === 'addMultipleQuestions'
    )?.id;

    // Refetch sidebar sessions when assessmentId changes (e.g. after role name is submitted)
    useEffect(() => {
      if (assessmentId) {
        fetchAssessmentSessions();
      }
    }, [assessmentId]);

    // Auto-scroll to bottom, but skip for jobCriteria component where user should start at top
    useEffect(() => {
      // Skip auto-scroll if the user has scrolled up to read earlier content
      if (!isNearBottomRef.current) return;

      // Get the last message to check its component type
      const lastMessage = messages[messages.length - 1];

      // Skip auto-scroll for jobCriteria component - user should see content from top
      const skipScrollComponentTypes = ['jobCriteria', 'optionsForm'];
      const shouldSkipScroll = lastMessage?.componentType && skipScrollComponentTypes.includes(lastMessage.componentType);

      if (shouldSkipScroll) {
        messagesEndRef.current?.parentElement?.scrollBy({ top: 150, behavior: 'smooth' });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }, [messages, isLoading, imageUploadScroll]);

    const handleMessagesScroll = useCallback(() => {
      const container = messagesContainerRef.current;
      if (!container) return;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      const nearBottom = distanceFromBottom < 120;
      isNearBottomRef.current = nearBottom;
      setShowScrollToBottom(!nearBottom && container.scrollHeight > container.clientHeight + 200);
    }, []);

    const scrollToBottom = useCallback(() => {
      isNearBottomRef.current = true;
      setShowScrollToBottom(false);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const hasTimelineComponent = messages.some(message => message.componentType === 'timeline');
    // The job assessment is what the timeline stage produces once it's submitted, so its
    // presence means that stage is behind the recruiter. Checked across the whole list
    // rather than "later than the timeline" because a single response can carry both, and
    // the job assessment gets pushed first — it isn't reliably ordered after the timeline.
    const hasJobAssessment = messages.some(message => message.componentType === 'jobAssessment');

    return (
      <>
        <ShortOnCreditModal
          isOpen={showShortOnCreditModal}
          onClose={() => setShowShortOnCreditModal(false)}
          creditBalance={creditErrorInfo?.creditsAvailable ?? creditsAvailable}
          creditsRequired={creditErrorInfo?.creditsRequired}
          description={
            conversationState === 'upload_cvs' || conversationState === 'upload_cv'
              ? `Uploading CVs requires ${creditErrorInfo?.creditsRequired ?? 0} credits (5 credits per CV). You currently have ${creditErrorInfo?.creditsAvailable ?? 0} credits available.`
              : conversationState === 'upload_jd' || conversationState === 'get_jd_upload'
                ? `Uploading a Job Description requires ${creditErrorInfo?.creditsRequired ?? 0} credits (3 credits per JD). You currently have ${creditErrorInfo?.creditsAvailable ?? 0} credits available.`
                : `This action requires ${creditErrorInfo?.creditsRequired ?? 0} credits. You currently have ${creditErrorInfo?.creditsAvailable ?? 0} credits available. Top up your credits to continue.`
          }
          onTopupCredit={() => {
            setShowShortOnCreditModal(false);
            setShowBuyCreditModal(true);
          }}
        />
        <BuyAdditionalCreditModal
          isOpen={showBuyCreditModal}
          onClose={() => setShowBuyCreditModal(false)}
          currentBalance={creditsAvailable}
          onPaymentSuccess={() => {
            setShowBuyCreditModal(false);
            handleRetry();
          }}
        />
        <HiringProgressModal
          isOpen={showHiringProgressFromHook}
          onClose={() => setShowHiringProgressFromHook(false)}
          creditsRemaining={creditsAvailable}
          creditsRequired={creditErrorInfo?.creditsRequired ?? 0}
          onTopupCredit={() => {
            setShowHiringProgressFromHook(false);
            setShowBuyCreditModal(true);
          }}
        />
        {conversationHistoryLoading ? (
          <div className={styles.loadingState}>
            <Spinner />
          </div>
        ) : (
          <div className={`${styles.chatSection} ${className}`}>
            <div
              className={styles.messagesContainer}
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
            >
              {messages.length === 0 ? (
                <div className={styles.emptyChat}>
                  <p>No messages yet. Start a conversation!</p>
                </div>
              ) : (
                messages.map((message, index) => {
                const hasLaterComponentMessage = messages.slice(index + 1).some(m => m.componentType);
                return (
                  <React.Fragment key={message.id}>
                    {!message.componentType && message.content !== '' && message.content !== 'Open-ended' && (
                      <MessageDisplay key={message.id} message={message} />
                    )}

                    {message.componentType === 'optionsForm' && (
                      <OptionsForm
                        key={assessmentId || 'new'}
                        onChange={handleFormChange}
                        className={styles.optionsForm}
                        onSalaryChange={handleSalaryChange}
                        assessmentId={assessmentId}
                        expectations={message.componentData.expectations}
                        formData={formData}
                        salaryDetails={salaryDetails}
                        sendRequest={(payload) => {
                          // The form's CTA submission triggers sendRequest internally; mirror what the
                          // backend records as the corresponding user-side option click so the bubble
                          // appears live, not just after a history reload.
                          const optionText = currentOptions?.[0]?.text;
                          if (optionText) {
                            setMessages([
                              ...messages,
                              {
                                id: `${Date.now()}-user-form-submit-${Math.random().toString(36).substr(2, 9)}`,
                                content: optionText,
                                sender: 'user',
                                timestamp: new Date(),
                              },
                            ]);
                          }
                          sendRequest(payload);
                        }}
                        fetchAssessmentSessions={fetchAssessmentSessions}
                        setIsLoading={setIsLoading}
                        questions={formatApiResponseToQuestions(message.componentData.expectations)}
                        isSubmitted={message.componentData.state !== conversationState}
                        historicalFormData={historicalFormData ? historicalFormData : message.componentData.historicalFormData || {}}
                        historicalSalaryDetails={message.componentData.historicalSalaryDetails || salaryDetails || null}
                      />
                    )}

                    {message.componentType === 'jobDescription' && (
                      <div className={styles.jobDescSection}>
                        <JobDescription
                          jdData={message.componentData.jdPreview}
                          assessmentId={assessmentId}
                          onEdit={hasLaterComponentMessage ? undefined : () => {}}
                          hideHeader
                        />
                      </div>
                    )}

                    {message.componentType === 'jobCriteria' && (
                      <div className={styles.jobCriteriaSection}>
                        <EnhancedJobCriteria
                          assessmentId={assessmentId}
                          initialData={mapJsonToJobCriteriaData(message.componentData.criteriaData)}
                          disabled={hasLaterComponentMessage}
                        />
                      </div>
                    )}
                    {message.componentType === 'questions' && (message.state === 'technical_competence' || message.state === 'review_questions') && (
                      <div className={styles.questionsSection}>
                        <QuestionsComponent
                          assessmentId={assessmentId}
                          questionsData={message.componentData.questions}
                          onQuestionsChange={handleTechnicalQuestionsChange}
                          disabled={hasLaterComponentMessage}
                        />
                      </div>
                    )}
                    {message.componentType === 'addSingleQuestion' && isCustomQuestionStage(message.state) && (
                      <div className={styles.questionsSection}>
                        <QuestionForm assessmentId={assessmentId} submitQuestion={submitQuestion} setImageUploadScroll={setImageUploadScroll} questionsData={message.componentData.questions} isSubmitted={hasLaterComponentMessage || message.id !== lastQuestionMessageId || isSubmittedQuestions} creditsAvailable={creditsAvailable} onInsufficientCredits={handleInsufficientCredits} />
                      </div>
                    )}
                    {message.componentType === 'addMultipleQuestions' && isCustomQuestionStage(message.state) && (
                      <div className={styles.questionsSection}>
                        <MultipleChoiceForm assessmentId={assessmentId} submitQuestion={submitQuestion} setImageUploadScroll={setImageUploadScroll} questionsData={message.componentData.questions} isSubmitted={hasLaterComponentMessage || message.id !== lastQuestionMessageId || isSubmittedQuestions} creditsAvailable={creditsAvailable} onInsufficientCredits={handleInsufficientCredits} />
                      </div>
                    )}
                    {message.componentType === 'timeline' && (message.state === 'done' || message.state === 'timeline') && (
                      <div className={styles.timelineSection}>
                        <TimelineComponent
                          assessmentId={assessmentId}
                          // Done is only for a timeline the recruiter still has to submit.
                          // History rehydration always marks the timeline message processed,
                          // so without these guards reopening a finished conversation shows a
                          // live Done button on a stage that's already been completed.
                          isSubmitted={message?.isProcessed && !hasJobAssessment && !hasLaterComponentMessage}
                          sendRequest={sendRequest}
                          hasJobAssessment={hasJobAssessment}
                        />
                      </div>
                    )}
                    {message.componentType === 'jobAssessment' && (
                      <div className={styles.jobAssessmentSection}>
                        <JobAssessment
                          assessmentId={assessmentId}
                          initialData={{
                            ...message.componentData,
                            candidatesToInvite: historyCandidatesToInvite.length > 0
                              ? historyCandidatesToInvite
                              : candidatesToInvite.length > 0
                                ? candidatesToInvite
                                : message.componentData?.candidatesToInvite,
                          }}
                          onSave={(updatedData) => { }}
                        />
                      </div>
                    )}
                    {message.componentType === 'experienceLevelForm' && (
                      <div className="my-4">
                        <ExperienceLevelForm
                          onSubmit={handleExperienceLevelSubmit}
                          isSubmitted={!message.isProcessed}
                          historicalData={message.componentData}
                          fetchAssessmentSessions={fetchAssessmentSessions}
                        />
                      </div>
                    )}
                    {message.componentType === 'assistantControl' && (
                      <div className="my-4">
                        <AssistantControlForm
                          control={message.componentData.control}
                          isSubmitted={hasLaterComponentMessage || !!message.componentData.isHistorical}
                          initialValues={message.componentData.submittedValues}
                          onSubmit={handleControlSubmit}
                        />
                      </div>
                    )}
                    {message.componentType === 'cvUpload' && (
                      <div className="my-4">
                        <CVUpload
                          onUpload={message.componentData.onUpload}
                          maxFiles={message.componentData.maxFiles || 5}
                        />
                      </div>
                    )}
                    {message.componentType === 'cvComparison' && (
                      <div className="my-4">
                        <CVComparisonTable
                          candidates={message.componentData.candidates}
                          disclaimer={message.componentData.disclaimer}
                          options={
                            conversationState !== 'display_results'
                              ? message.componentData.options?.filter((opt: any) => opt.value !== 'administer_assessment')
                              : message.componentData.options
                          }
                          onOptionClick={handleOptionSelect}
                          onCandidatesSelected={handleCandidatesToInviteChange}
                        />
                      </div>
                    )}
                    {message.componentType === 'uploadedCVs' && (
                      <div className="my-4">
                        <UploadedCVDisplay files={message.componentData.files} />
                      </div>
                    )}
                    {message.componentType === 'errorWithRetry' && (
                      <ErrorWithRetry
                        message={message.content}
                        onRetry={handleRetry}
                        onStartNew={handleStartNewConversation}
                      />
                    )}
                  </React.Fragment>
                );
              })
              )}

              {isLoading && (
                <LoadingIndicator phase={loadingPhase} />
              )}
              <div ref={messagesEndRef} />
            </div>

            {showScrollToBottom && (
              <button
                type="button"
                className={styles.scrollToBottomBtn}
                onClick={scrollToBottom}
                aria-label="Scroll to latest message"
              >
                <ArrowDown/>
              </button>
            )}

            {showInput && !isLoading && (
              <MessageForm
                onSendMessage={handleRoleAwareSend}
                placeholder={inputHints?.placeholder || placeholder}
                className=""
                conversationState={conversationState}
                documentMode={inputHints?.documentMode === true}
                allowUpload={inputHints ? inputHints.allowUpload !== false : true}
                maxFiles={conversationState === 'upload_jd' || conversationState === 'get_jd_upload' ? 1 : 5}
                initialValue={conversationState === 'get_role' ? (prefillRole ?? undefined) : undefined}
              />
            )}

            {!isLoading && showOption && currentOptions && currentOptions.length > 0 && !currentOptions.some(opt => opt.value === 'submit_open_ended' || opt.value === 'submit_multiple_choice' || opt.value === 'publish_assessment') && (currentOptions[0].value !== 'done' || currentOptions[0].text === "No I'm done") && currentOptions[0].value !== 'administer_assessment' && !hasTimelineComponent && (
              <div className={styles.recruitmentOptions}>
                <RecruitmentOptions
                  onOptionSelect={handleOptionSelect}
                  options={currentOptions}
                />
              </div>
            )}
          </div>
        )}
      </>
    );
  });

export { ChatMessages };
